import { useEffect, useRef, useState } from 'react'

import { motion } from 'framer-motion'

const MILESTONES = [0, 25, 50, 75, 98, 100]
const VIDEO_TRACKING_INTERVAL = 5 * 60 * 1000 // 5 minutes

interface VideoPlayerProps {
  src: string
  onTrackAction?: () => void
  onPercentageWatchedMilestone?(milestone: number, video: HTMLVideoElement): void
  disableAdvancedControls?: boolean
}

export default function VideoPlayer({
  src,
  onTrackAction,
  onPercentageWatchedMilestone,
  disableAdvancedControls = false,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const milestonesRef = useRef<Set<number>>(new Set())
  const callbackRef = useRef(onPercentageWatchedMilestone)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  callbackRef.current = onPercentageWatchedMilestone

  useEffect(() => {
    milestonesRef.current.clear()
  }, [src])

   // ✅ Array-based event handling for user actions
   useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Define events and their specific handlers
    const videoEvents = [
      {
        event: 'play',
        handler: () => {
          onTrackAction?.()  // Track immediately

          // Start 3-minute interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
          }
          intervalRef.current = setInterval(() => {
            onTrackAction?.()
          }, VIDEO_TRACKING_INTERVAL)
        }
      },
      {
        event: 'pause',
        handler: () => {
          onTrackAction?.()  // Track immediately

          // Stop interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        }
      },
      {
        event: 'ended',
        handler: () => {
          // Stop interval (no tracking on end)
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        }
      },
    ]

    // Register all events
    videoEvents.forEach(({ event, handler }) => {
      video.addEventListener(event, handler)
    })

    // Cleanup all events and interval
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      videoEvents.forEach(({ event, handler }) => {
        video.removeEventListener(event, handler)
      })
    }
  }, [onTrackAction])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => {
      if (!video.duration || !callbackRef.current) return

      for (const milestone of MILESTONES) {
        if (milestonesRef.current.has(milestone)) continue

        const progress = Math.round((video.currentTime / video.duration) * 100)
        if (progress >= milestone) {
          milestonesRef.current.add(milestone)
          callbackRef.current(milestone, video)
        }
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [])

  return (
    <motion.video
      ref={videoRef}
      controls
      controlsList={disableAdvancedControls ? 'nodownload' : undefined}
      disablePictureInPicture={disableAdvancedControls}
      src={src}
      initial={{ opacity: 0 }}
      animate={{ opacity: videoLoaded ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      onLoadedData={() => setVideoLoaded(true)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  )
}
